import axios from 'axios';

import apiFn from './api-service';

export enum Status {
  Finalized = 'finalized',
  Cancelled = 'cancelled',
}

// This is copied from AntD's rc-uploaded dependency
export interface UploadProgressEvent extends Partial<ProgressEvent> {
  percent?: number;
}

export type MimeType = 'image/jpeg' | 'image/png' | 'image/gif' | 'image/bmp' | 'image/webp';
export type EntityType = 'activity_feed' | 'company' | 'profile';
export type ResourceType = 'events' | 'eventapp' | 'organizations';

export interface Image {
  id: string;
  externalId: string | null;
  name: string;
  mimeType: MimeType;
  length: number;
  cropX: number | null;
  cropY: number | null;
  cropWidth: number | null;
  cropHeight: number | null;
  imageUrl: string | null;
  status: 'pending' | 'finalized' | 'cancelled';
  type: EntityType;
}

const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
const ALLOWED_STATUSES = [Status.Finalized, Status.Cancelled];

const api = (resourceId, resourceType, token = '') => {
  let headers: Record<string, string> = {};
  // If token is provided it is called from self edit and needs Authorization header
  if (token) {
    headers = { Authorization: `self-edit ${token}` };
  }
  return apiFn(`/api/uapi/${resourceType}/${resourceId}/images/`, headers);
};

export const get = (
  resourceId,
  imageId,
  params = {},
  token = '',
  resourceType = 'events',
): Promise<Image> =>
  api(resourceId, resourceType, token)
    .get(imageId.toString(10), { params })
    .then(response => response.data.data);

export const list = (resourceId, params = {}, resourceType = 'events'): Promise<Image[]> =>
  api(resourceId, resourceType)
    .get('', { params: { ...params } })
    .then(response => response.data.data);

const update = (resourceId, id, params = {}, token = '', resourceType = 'events'): Promise<Image> =>
  api(resourceId, resourceType, token)
    .patch(id, params)
    .then(response => response.data.data);

export const duplicate = (resourceId, id, params = {}, resourceType = 'events'): Promise<Image> =>
  api(resourceId, resourceType)
    .post(`${id}/duplicate`, params)
    .then(response => response.data.data);

export const notify = (
  resourceId,
  imageId,
  status = Status.Finalized,
  token = '',
  resourceType = 'events',
): Promise<Image> => {
  if (typeof imageId === 'undefined' || imageId === null) {
    throw new Error('file id cannot be either undefined or null');
  }

  if (!ALLOWED_STATUSES.includes(status)) {
    throw new Error(`invalid status, allowed status: ${ALLOWED_STATUSES.join(', ')}`);
  }

  return update(resourceId, imageId, { status }, token, resourceType);
};

export const crop = (
  resourceId,
  imageId,
  { x: cropX, y: cropY, height: cropHeight, width: cropWidth },
  token = '',
  resourceType = 'events',
): Promise<Image> => {
  const payload = {
    cropX,
    cropY,
    cropHeight,
    cropWidth,
  };

  return update(resourceId, imageId, payload, token, resourceType);
};

interface CreateResponse extends Image {
  status: 'pending';
  upload: {
    url: string;
    expiry: number;
  };
}

export const create = (
  resourceId: string,
  name: string,
  mimeType: MimeType,
  length: number,
  type: EntityType,
  token = '',
  resourceType: ResourceType = 'events',
): Promise<CreateResponse> => {
  if (typeof name === 'undefined' || name === null) {
    throw new Error('image name cannot be either undefined or null');
  }

  if (typeof mimeType === 'undefined' || mimeType === null) {
    throw new Error('image mime type cannot be either undefined or null');
  }

  if (!ALLOWED_MIME_TYPES.includes(mimeType)) {
    throw new Error(`invalid mime type, allowed mime types: ${ALLOWED_MIME_TYPES.join(', ')}`);
  }

  const payload = {
    name,
    length,
    type,
    mimeType,
  };

  return api(resourceId, resourceType, token)
    .post(null, payload)
    .then(response => response.data.data);
};

export const upload = (
  url: string,
  file: any,
  onProgress: (progress: UploadProgressEvent) => void = () => {},
) => {
  if (typeof url === 'undefined' || url === null) {
    throw new Error('image upload url cannot either undefined or null');
  }

  if ('File' in window && !(file instanceof window.File)) {
    throw new Error('invalid file: it should be instance of window.File');
  }

  if (typeof onProgress !== 'function') {
    throw new Error('onProgress callback is not a function');
  }

  return axios.put(url, file, {
    headers: { 'Content-Type': file.type },
    onUploadProgress: onProgress,
  });
};

export const remove = (resourceId, imageId, token = '', resourceType = 'events') =>
  api(resourceId, resourceType, token)
    .delete(imageId)
    .then(response => response.data.data);

export const preview = (
  resourceId,
  imageId,
  params: any = {},
  token = '',
  resourceType = 'events',
) => {
  if (typeof resourceId === 'undefined') {
    throw new Error('resource id is required');
  }

  if (typeof imageId === 'undefined') {
    throw new Error('image id is required');
  }

  if (
    typeof params.x === 'undefined' ||
    typeof params.y === 'undefined' ||
    typeof params.height === 'undefined' ||
    typeof params.width === 'undefined'
  ) {
    throw new Error('params are required to have x, y, height, and width values');
  }

  const { x, y, height, width } = params;

  const start = [Number.parseInt(x, 10), Number.parseInt(y, 10)];
  const end = [Number.parseInt(x + width, 10), Number.parseInt(y + height, 10)];

  if ([...start, ...end].reduce((prev, next) => prev || Number.isNaN(next), false)) {
    throw new Error('x, y, height, and width should be valid integers');
  }

  return get(resourceId, imageId, { pre_crop: [...start, ...end].join(',') }, token, resourceType);
};
